import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { AuthService } from '../services/authService';

interface ResetPasswordPageProps {
  onDone: () => void;
}

const ResetPasswordPage: React.FC<ResetPasswordPageProps> = ({ onDone }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);
    const { error: updateError } = await AuthService.updatePassword(password);
    setIsLoading(false);

    if (updateError) {
      setError(updateError);
      return;
    }

    await AuthService.logout();
    window.history.replaceState({}, '', '/');
    onDone();
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url('https://events.logisticsmarket.rs/wp-content/uploads/2025/09/LM-layout.png')`
      }}
    >
      <div className="absolute inset-0 bg-white bg-opacity-60"></div>
      <div className="absolute inset-0" style={{ backgroundColor: 'rgb(0 0 0 / 40%)' }}></div>

      <div className="relative z-10 bg-white rounded-lg shadow-2xl p-8 w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <img
            src="https://events.logisticsmarket.rs/wp-content/uploads/2022/02/Logistics-Market-logo-6.webp"
            alt="Logistics Market Logo"
            className="h-20 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">New password</h1>
          <p className="text-sm text-gray-600">Choose a new password for your account.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          <div>
            <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
              New password
            </label>
            <div className="relative">
              <input
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-yellow-600"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
              Confirm password
            </label>
            <input
              id="confirm-password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-yellow-600"
              autoComplete="new-password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full text-white py-3 px-4 rounded-md font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 transition duration-200"
            style={{ backgroundColor: '#C89116' }}
          >
            {isLoading ? 'SAVING...' : 'SAVE PASSWORD'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
